import { useEffect, useRef } from 'react';
import { getCachedUser } from '../utils/userCache';
import socketService from '@/services/socket';

interface UseAutoStatusOptions {
  idleTimeout?: number; // milliseconds
  onStatusChange?: (status: string) => void;
}

const STATUS_UPDATE_DEBOUNCE_MS = 30000; // 30 seconds - batch status updates

// Module-level flag to ensure listeners are only registered once
let listenersRegistered = false;
const activeInstances = new Set<number>();
let instanceCounter = 0;

export const useAutoStatus = ({
  idleTimeout = 5 * 60 * 1000,
  onStatusChange,
}: UseAutoStatusOptions = {}) => {
  const instanceIdRef = useRef<number>(instanceCounter++);
  const lastActivityRef = useRef(Date.now());
  const statusRef = useRef<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastStatusUpdateRef = useRef<number>(0);
  const pendingStatusRef = useRef<string | null>(null);
  const debouncedUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Register this instance
    activeInstances.add(instanceIdRef.current);

    // Only set up listeners if this is the first instance
    if (listenersRegistered) {
      console.debug(
        '[useAutoStatus] Listeners already registered, skipping setup for instance',
        instanceIdRef.current,
      );
      return () => {
        activeInstances.delete(instanceIdRef.current);
      };
    }

    listenersRegistered = true;
    console.debug(
      '[useAutoStatus] Registering global listeners for instance',
      instanceIdRef.current,
    );
    const updateStatus = (newStatus: string, immediate = false) => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      if (statusRef.current !== newStatus) {
        statusRef.current = newStatus;
        const user = getCachedUser();

        // Don't override DND status unless explicitly set
        if (user.status === 'dnd' && newStatus !== 'dnd') return;

        const now = Date.now();
        const timeSinceLastUpdate = now - lastStatusUpdateRef.current;

        // If immediate or enough time has passed, update now
        if (immediate || timeSinceLastUpdate >= STATUS_UPDATE_DEBOUNCE_MS) {
          socketService.emit('user:status-changed', newStatus);
          if (user && user.id) {
            localStorage.setItem('user', JSON.stringify({ ...user, status: newStatus }));
            window.dispatchEvent(new Event('user-updated'));
          }
          onStatusChange?.(newStatus);
          lastStatusUpdateRef.current = now;
          pendingStatusRef.current = null;

          // Clear any pending debounced update
          if (debouncedUpdateTimerRef.current) {
            clearTimeout(debouncedUpdateTimerRef.current);
            debouncedUpdateTimerRef.current = null;
          }
        } else {
          // Store pending status and schedule debounced update
          pendingStatusRef.current = newStatus;

          if (!debouncedUpdateTimerRef.current) {
            const remainingTime = STATUS_UPDATE_DEBOUNCE_MS - timeSinceLastUpdate;
            debouncedUpdateTimerRef.current = setTimeout(() => {
              if (pendingStatusRef.current) {
                socketService.emit('user:status-changed', pendingStatusRef.current);
                const updatedUser = getCachedUser();
                if (updatedUser && updatedUser.id) {
                  localStorage.setItem(
                    'user',
                    JSON.stringify({ ...updatedUser, status: pendingStatusRef.current }),
                  );
                  window.dispatchEvent(new Event('user-updated'));
                }
                onStatusChange?.(pendingStatusRef.current);
                lastStatusUpdateRef.current = Date.now();
                pendingStatusRef.current = null;
              }
              debouncedUpdateTimerRef.current = null;
            }, remainingTime);
          }
        }
      }
    };

    const checkIdleStatus = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      // Check if user is in a voice call
      const inVoiceCall = sessionStorage.getItem('inVoiceCall') === 'true';

      // Force online status if in voice call
      if (inVoiceCall) {
        updateStatus('online', true);
        return;
      }

      if (timeSinceActivity >= idleTimeout) {
        updateStatus('idle', true);
      } else {
        updateStatus('online', false);
      }
    };

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (statusRef.current === 'idle') {
        updateStatus('online', true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - don't change to offline immediately, let idle timer handle it
        return;
      } else {
        // Tab is visible again - update immediately
        handleActivity();
        updateStatus('online', true); // Immediate update when tab becomes visible
      }
    };

    // Activity listeners - including typing, click, custom user-activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'touchmove', 'click', 'user-activity'];
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check idle status every 15 seconds
    const interval = setInterval(checkIdleStatus, 15000);
    timerRef.current = interval;

    // Initial status update (immediate on app load)
    updateStatus('online', true);

    // Cleanup
    return () => {
      activeInstances.delete(instanceIdRef.current);

      // Only remove listeners if this was the last active instance
      if (activeInstances.size === 0) {
        console.debug('[useAutoStatus] Last instance unmounting, cleaning up global listeners');
        events.forEach((event) => {
          document.removeEventListener(event, handleActivity);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        if (debouncedUpdateTimerRef.current) {
          clearTimeout(debouncedUpdateTimerRef.current);
        }
        listenersRegistered = false;
      }
    };
  }, [idleTimeout, onStatusChange]);

  return statusRef;
};
